// A coach adjustment awaiting a decision: the brief, each changed day as
// BEFORE → AFTER against the weekly plan, and APPLY / DISCARD.
//
// The proposal hides itself once none of its sessions are open any more —
// the same test the server's /api/coach/adjust/open uses — so every surface
// that renders it closes together after a decision, without waiting on the
// open-adjustment query to refetch.

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient, formatApiError } from '../lib/api';
import { useBulkApplyMutation } from '../lib/coach-actions';
import { useDiscardAdjustment } from '../lib/coach-adjust';
import { useCoachLatest } from '../lib/day-data';
import { C, FONT_MONO } from '../lib/design';
import { dayLabel } from '../lib/ledger';
import {
  CoachLatestResponseSchema,
  type CoachAdjustResponse,
  type CoachLatestResponse,
  type CoachLatestSuggestion,
} from '../lib/types';
import { CoachBriefView } from './CoachBriefView';
import { Btn, ErrorStrip, Lbl, Note, Sub } from './ledger/ui';

// Suggestion payloads are `unknown` on the wire; these are the activity
// fields the comparison reads.
const ActivityPayloadSchema = z.object({
  date: z.string(),
  kind: z.string().optional(),
  name: z.string().optional(),
  reason: z.string().optional(),
});

type SessionRow = { date: string; name: string; reason: string | undefined };

function activityRows(suggestions: CoachLatestSuggestion[]): SessionRow[] {
  const rows: SessionRow[] = [];
  for (const s of suggestions) {
    if (s.dimension !== 'activity') continue;
    const parsed = ActivityPayloadSchema.safeParse(s.suggestion);
    if (!parsed.success) continue;
    const p = parsed.data;
    rows.push({ date: p.date, name: p.kind === 'rest' ? 'Rest' : p.name ?? 'Activity', reason: p.reason });
  }
  return rows;
}

export function AdjustmentProposal({ coachCallId }: { coachCallId: number }) {
  // Same key /review uses for a specific call, so the two share one cache
  // entry and the ['coach-latest'] prefix invalidations refresh both.
  const proposal = useQuery<CoachLatestResponse>({
    queryKey: ['coach-latest', coachCallId],
    queryFn: () =>
      apiClient.get<CoachLatestResponse>(`/api/coach/latest?coach_call_id=${coachCallId}`, {
        schema: CoachLatestResponseSchema,
      }),
  });
  const weekly = useCoachLatest();
  const apply = useBulkApplyMutation();
  const discard = useDiscardAdjustment();
  const busy = apply.isPending || discard.isPending;
  const actionError = apply.error ?? discard.error;

  if (proposal.isLoading) return <Sub>LOADING ADJUSTMENT…</Sub>;
  if (proposal.error) return <ErrorStrip message={formatApiError(proposal.error)} />;
  const data = proposal.data;
  // Closed once no session is pending or snoozed: applied or discarded.
  const open = data?.suggestions.some(
    (s) =>
      (s.dimension === 'activity' || s.dimension === 'details') &&
      (s.status === 'pending' || s.status === 'snoozed'),
  );
  if (!data || !open) return null;

  const before: Record<string, string> = {};
  for (const row of activityRows(weekly.data?.suggestions ?? [])) before[row.date] ??= row.name;
  const after = activityRows(data.suggestions).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div
      style={{
        border: `1px solid ${C.line}`,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        fontFamily: FONT_MONO,
      }}
    >
      <Lbl color={C.signal}>◆ ADJUSTMENT</Lbl>
      {data.coach_call.brief && <CoachBriefView brief={data.coach_call.brief} />}
      <div>
        {after.map((p) => (
          <div key={p.date} style={{ borderTop: `1px solid ${C.line}`, padding: '8px 0' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '96px 1fr',
                gap: 8,
                alignItems: 'baseline',
              }}
            >
              <Lbl>{dayLabel(p.date)}</Lbl>
              <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                <span style={{ color: C.dim }}>{before[p.date] ?? '—'}</span>
                <span style={{ color: C.faint }}> → </span>
                <span style={{ color: C.text }}>{p.name}</span>
              </div>
            </div>
            {p.reason && (
              <div
                style={{
                  fontSize: 11,
                  color: C.dim,
                  lineHeight: 1.55,
                  marginTop: 4,
                  paddingLeft: 104,
                }}
              >
                {p.reason}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <Btn primary disabled={busy} onClick={() => apply.mutate({ coachCallId })}>
          {apply.isPending ? 'APPLYING…' : 'APPLY ADJUSTMENT'}
        </Btn>
        <Btn disabled={busy} onClick={() => discard.mutate({ coachCallId })}>
          {discard.isPending ? 'DISCARDING…' : 'DISCARD'}
        </Btn>
      </div>
      <Sub color={C.faint} style={{ lineHeight: 1.5 }}>
        Discarding keeps the constraint: the next weekly plan still respects it. Remove it in
        Settings → Vacation Windows.
      </Sub>
      {actionError && <ErrorStrip message={formatApiError(actionError)} />}
    </div>
  );
}

/** What an adjustment request came back with: a proposal to decide on, a
 *  "nothing to change" brief, or the failure. */
export function AdjustmentOutcome({
  data,
  error,
}: {
  data: CoachAdjustResponse | undefined;
  error: Error | null;
}) {
  if (error) return <ErrorStrip message={formatApiError(error)} />;
  if (!data) return null;
  if (data.status === 'no_change') {
    return <Note label="NO CHANGE NEEDED">{data.brief.headline}</Note>;
  }
  return <AdjustmentProposal coachCallId={data.coach_call_id} />;
}
